import React, { useEffect, useMemo, useState } from "react"
import { listAdminEvents } from "../../services/events"
import { Link } from "react-router-dom"
import {
    CalendarDaysIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    TicketIcon
} from "@heroicons/react/24/outline"

const AdminEvents = () => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [query, setQuery] = useState("")

    const fetchItems = async () => {
        setLoading(true)
        setError("")
        try {
            const res = await listAdminEvents()
            setLoading(false)
            if (res.status === 200) {
                setItems(Array.isArray(res.data) ? res.data : [])
            } else {
                setError(res.error || 'Failed to load events')
            }
        } catch (err) {
            setLoading(false)
            setError('An unexpected error occurred')
        }
    }

    useEffect(() => { fetchItems() }, [])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return items
        return items.filter(e => (
            (e.title || '').toLowerCase().includes(q) ||
            (e.venue || '').toLowerCase().includes(q) ||
            (e.centerId?.organization || '').toLowerCase().includes(q) ||
            (e.description || '').toLowerCase().includes(q) ||
            (e.location?.city || '').toLowerCase().includes(q) ||
            (e.location?.state || '').toLowerCase().includes(q)
        ))
    }, [items, query])

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">Events</h1>
                <p className="text-slate-500 mt-1">Manage all events across the platform</p>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="relative w-full max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search events, venues, organizers..."
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm"
                    />
                </div>
                <button onClick={fetchItems} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                    <p className="mt-2 text-slate-500">Loading events...</p>
                </div>
            ) : error ? (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
                    {error}
                    <button onClick={fetchItems} className="block mx-auto mt-2 text-sm font-bold hover:underline">Try Again</button>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Event</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Center</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Date & Location</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Status</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                            No events found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(event => (
                                        <tr key={event._id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                                                        {event.coverImage ? (
                                                            <img src={event.coverImage} alt={event.title} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                                <CalendarDaysIcon className="w-6 h-6" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-semibold text-slate-900 line-clamp-1">{event.title}</div>
                                                        {event.description && (
                                                            <div className="text-xs text-slate-500 line-clamp-2 mt-1 max-w-[300px]">
                                                                {event.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-slate-600">
                                                <div className="font-medium">
                                                    {event.centerId?.organization || 'Unknown Center'}
                                                </div>
                                                {event.centerId?.email && (
                                                    <div className="text-xs text-slate-400 mt-1">
                                                        {event.centerId.email}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-slate-600">
                                                <div className="flex flex-col gap-1 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <CalendarDaysIcon className="w-4 h-4 text-slate-400" />
                                                        <span className="font-medium">
                                                            {event.startsAt ? new Date(event.startsAt).toLocaleDateString('en-US', { 
                                                                year: 'numeric', 
                                                                month: 'short', 
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            }) : 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                                                        <span className="truncate max-w-[200px]" title={event.venue}>
                                                            {event.venue}
                                                        </span>
                                                    </div>
                                                    {event.location && (event.location.city || event.location.state || event.location.country) && (
                                                        <div className="text-xs text-slate-400 ml-5">
                                                            {[event.location.city, event.location.state, event.location.country].filter(Boolean).join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${event.status === 'PUBLISHED' ? 'bg-green-50 text-green-700 border-green-200' :
                                                        event.status === 'DRAFT' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                                                            'bg-red-50 text-red-700 border-red-200'
                                                    }`}>
                                                    {event.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Link
                                                    to={`/admin/events/${event._id}`}
                                                    className="text-cyan-600 hover:text-cyan-800 font-medium hover:underline"
                                                >
                                                    Manage
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminEvents
